import { TZDateMini } from '@date-fns/tz';
import { createSelector } from '@reduxjs/toolkit';
import { addDays, format, parse } from 'date-fns';
import ooeConstants from '../constants';
import {
  selectDeliveryAddressValid,
  selectDestination,
  selectDetailsDate,
  selectDetailsFormValues,
  selectDetailsTime,
  selectIsEditCMTOrder,
  selectPaperGoodsOptions,
  selectPaperGoodsYesOrNo,
  selectReorder,
} from '../order/selectors';
import {
  selectBypassBusinessRules,
  selectCateringDeliveryMOSettings,
  selectCateringPickupMOSettings,
  selectDeliveryLeadTime,
  selectLocationTimezone,
  selectPickupLeadTime,
} from '../user/selectors';
import { getVariation } from '../services/launchDarkly';
import { formatPaper, formatPaperGoodsOptions } from '../util/format';
import { selectCartHasBreakfastItems, selectCartItemsLength } from '../cart/selectors';
import type { State as EventDetailsState } from './reducer';

export const selectLeadTimeWarning = createSelector(
  selectDestination,
  selectPickupLeadTime,
  selectDeliveryLeadTime,
  (destination, pickupLeadTime, deliveryLeadTime) => {
    if (destination === ooeConstants.DELIVERY) {
      return {
        type: 'warning',
        message: deliveryLeadTime,
      };
    }
    return {
      type: 'warning',
      message: pickupLeadTime,
    };
  },
);

export const selectSpecialEvents = createSelector(
  selectDestination,
  selectCateringDeliveryMOSettings,
  selectCateringPickupMOSettings,
  (destination, cateringDelivery, cateringPickup) => {
    if (destination === ooeConstants.DELIVERY) {
      return cateringDelivery?.specialEvents ?? [];
    }

    if (destination === ooeConstants.PICKUP) {
      return cateringPickup?.specialEvents ?? [];
    }
    return [];
  },
);

export const selectHoursOfOperation = createSelector(
  selectDestination,
  selectCateringDeliveryMOSettings,
  selectCateringPickupMOSettings,
  (destination, cateringDelivery, cateringPickup) => {
    let serviceHours;
    if (destination === ooeConstants.DELIVERY) {
      serviceHours = cateringDelivery?.hoursOfOperation;
    } else if (destination === ooeConstants.PICKUP) {
      serviceHours = cateringPickup?.hoursOfOperation;
    }
    return serviceHours;
  },
);

export const selectLeadTimeMinutes = createSelector(
  selectDestination,
  selectCateringDeliveryMOSettings,
  selectCateringPickupMOSettings,
  (destination, cateringDelivery, cateringPickup) => {
    if (destination === ooeConstants.DELIVERY) {
      return cateringDelivery?.leadTimeMinutes ?? undefined;
    }

    if (destination === ooeConstants.PICKUP) {
      return cateringPickup?.leadTimeMinutes ?? undefined;
    }
    return;
  },
);

export const selectTimes = createSelector(
  selectBypassBusinessRules,
  selectDetailsDate,
  selectDetailsTime,
  selectCartHasBreakfastItems,
  selectCartItemsLength,
  selectLocationTimezone,
  (bypassBusinessRules, date, time, containsBreakfast, cartLength, timeZone) => {
    let promiseDateTime;
    let promiseTimeType;
    let businessDate;
    const automaticTime = () => {
      const restaurantNow = TZDateMini.tz(timeZone);
      return format(
        // default time for initializing orders that don't have promiseDateTime, with this set to next day
        // 'promiseDateTime too early' errors can be generated from order api which causes issues
        // see CMT-1386 for more details: https://cfacorp.atlassian.net/browse/CMT-1386
        addDays(
          new TZDateMini(
            restaurantNow.getFullYear(),
            restaurantNow.getMonth(),
            restaurantNow.getDate(),
            1,
            0,
            0,
            timeZone,
          ),
          2,
        ),
        ooeConstants.DATE_TIME_FORMAT.dateTime,
      );
    };
    if (date) {
      businessDate = format(date, ooeConstants.DATE_TIME_FORMAT.date);
    }
    if (date && time) {
      promiseDateTime = format(
        parse(time, ooeConstants.DATE_TIME_FORMAT.time, date),
        ooeConstants.DATE_TIME_FORMAT.dateTime,
      );
    } else if (bypassBusinessRules && !date) {
      promiseDateTime = automaticTime();
    } else if (bypassBusinessRules && date) {
      promiseDateTime = format(
        new TZDateMini(date.getFullYear(), date.getMonth(), date.getDate(), 1, 0, 0, timeZone),
        ooeConstants.DATE_TIME_FORMAT.dateTime,
      );
    } else if (cartLength > 0) {
      promiseTimeType = containsBreakfast
        ? ooeConstants.EARLIEST_AVAILABLE_BREAKFAST
        : ooeConstants.EARLIEST_AVAILABLE;
      //this is added because an order api respond with an error if we send only promiseTimeType when the BBR off and item is added to the cart
      promiseDateTime = automaticTime();
    }
    return {
      promiseDateTime,
      promiseTimeType,
      businessDate,
      date,
      time,
    };
  },
);

export const selectEventDetails = createSelector(
  selectDetailsFormValues,
  selectTimes,
  selectPaperGoodsOptions,
  selectPaperGoodsYesOrNo,
  selectIsEditCMTOrder,
  selectReorder,
  (details, times, paperGoodsOptions, paperGoodsYesOrNo, isCmtEditing, isReorder) => {
    const { paperGoods, specialInstructions, guestCount, cateringReason } = details;
    const { promiseDateTime, promiseTimeType, businessDate } = times;

    // The logic here is convoluted due to limitations in the orderAPI and the way our UI handles paper goods:
    // - The orderAPI only supports a boolean value for paperGoods
    // - Our UI also allows users to select specific paper goods (plates, cups, napkins, cutlery)
    // - To bridge this gap, we inject the selected paper goods text into the special instructions

    // Editing and reordering introduces additional complexity:
    // - When editing an order, we do not allow users to modify paper goods in the UI because the API only
    //   gives us a boolean and we don't reverse-engineer the special instructions text back to the UI
    // - When reordering, we carry over the user's special instructions from the original order (stripped
    //   of any paper goods text) so the user does not have to retype them. Paper goods options are reset
    //   to empty so the user must make a fresh selection.

    // Logic summary:
    // - If we are not editing or are reordering, the special instructions text is based on the current UI values
    //   combined with any additional instructions provided by the user
    // - If we are editing, we use the existing special instructions directly (if available)
    //   otherwise, we default to a generic "no paper goods" message.
    // - When showPaperGoods is on, paper-goods text is not copied into special instructions
    //   (it is sent via the paperGoods boolean instead) so confirmation emails will not duplicate it.
    const showPaperGoods = getVariation('showPaperGoods', false);
    const specialInstructionsMessage = showPaperGoods
      ? (specialInstructions || '').trim()
      : !isCmtEditing || isReorder
        ? `${formatPaperGoodsOptions(paperGoodsOptions)} ${specialInstructions || ''}`
        : specialInstructions || formatPaper(!!paperGoods);

    // If we are not editing or we are reordering set based on the UI values
    // otherwise set based on the paperGoods data from the order
    const paperGoodsBool = !isCmtEditing || isReorder ? paperGoodsYesOrNo === 'yes' : !!paperGoods;

    return {
      specialInstructions: specialInstructionsMessage,
      paperGoods: paperGoodsBool,
      guestCount: guestCount ? Number(guestCount) : undefined,
      businessDate,
      promiseDateTime,
      promiseTimeType,
      cateringReason: cateringReason || '',
    };
  },
);

export const selectEventDetailsState = (state: { eventDetails: EventDetailsState }) => state.eventDetails;
export const selectAddressSearchValue = createSelector(
  selectEventDetailsState,
  (eventDetails) => eventDetails.addressSearchValue,
);
export const selectAddressSearchResults = createSelector(
  selectEventDetailsState,
  (eventDetails) => eventDetails.addressSearchResults,
);
export const selectAddressSearchLoading = createSelector(
  selectEventDetailsState,
  (eventDetails) => eventDetails.loading.addressSearch,
);
export const selectAddressSearchError = createSelector(
  selectEventDetailsState,
  (eventDetails) => eventDetails.error.addressSearch,
);
export const selectAddressValidationLoading = createSelector(
  selectEventDetailsState,
  (eventDetails) => eventDetails.loading.addressValidation,
);
export const selectAddressValidationError = createSelector(
  selectEventDetailsState,
  (eventDetails) => eventDetails.error.addressValidation,
);
export const selectAddressSearchOpen = createSelector(
  selectEventDetailsState,
  (eventDetails) => eventDetails.addressSearchOpen,
);
export const selectAddressSearchBlurred = createSelector(
  selectEventDetailsState,
  (eventDetails) => eventDetails.addressSearchBlurred,
);
export const selectAddressSearchSeededFromEdit = createSelector(
  selectEventDetailsState,
  (eventDetails) => eventDetails.addressSearchSeededFromEdit,
);
export const selectAddressSearchSelectedPlaceId = createSelector(
  selectEventDetailsState,
  (eventDetails) => eventDetails.addressSearchSelectedPlaceId,
);
export const selectAddressSearchSelectedResult = createSelector(
  selectAddressSearchResults,
  selectAddressSearchSelectedPlaceId,
  (results, placeId) => results.find((result) => result.placeId === placeId),
);
/**
 * Whether the address search field should show its "invalid address" state.
 *
 * True when the field has text but no result was selected from the dropdown, and either:
 *  - the field has been blurred (the user left it without picking a result), or
 *  - a committed delivery address is still valid (the text was edited after a selection,
 *    so the committed address is stale).
 *
 * Always false for addresses seeded from an edited order or past address (those are
 * already validated, and may not have a placeId), until the user types in the field.
 */
export const selectAddressSearchInvalid = createSelector(
  selectAddressSearchBlurred,
  selectAddressSearchSelectedPlaceId,
  selectAddressSearchValue,
  selectDeliveryAddressValid,
  selectAddressSearchSeededFromEdit,
  (blurred, selectedPlaceId, value, deliveryAddressValid, seededFromEdit) =>
    !seededFromEdit && !selectedPlaceId && value.trim().length > 0 && (blurred || deliveryAddressValid),
);
